import { useEffect, useMemo, useRef, useState } from "react";
import { Stepper } from "../../components/UI/Stepper";
import {
    WASTE_RETURN_IMPORT_STEP_LABELS,
    WASTE_RETURN_IMPORT_STEPS,
    advanceFurthestVisitedOnNavigation,
    type WasteReturnImportStep,
} from "./wasteReturnImportSteps";

export interface WasteReturnImportStepperProps {
    currentStep: WasteReturnImportStep;
    maxReachableStep: WasteReturnImportStep;
    furthestCompletedStep: WasteReturnImportStep;
    activeTemplateId: string | null;
    journeyReady: boolean;
    onStepClick: (step: WasteReturnImportStep) => void;
}

export function WasteReturnImportStepper({
    currentStep,
    maxReachableStep,
    furthestCompletedStep,
    activeTemplateId,
    journeyReady,
    onStepClick,
}: WasteReturnImportStepperProps) {
    const [furthestVisitedStep, setFurthestVisitedStep] = useState<WasteReturnImportStep>("upload");
    const seededTemplateId = useRef<string | null>(null);
    const skipNextNavigationAdvance = useRef(false);

    useEffect(() => {
        if (!journeyReady) {
            return;
        }

        if (activeTemplateId === null) {
            seededTemplateId.current = null;
            skipNextNavigationAdvance.current = true;
            setFurthestVisitedStep("upload");
            return;
        }

        if (seededTemplateId.current !== activeTemplateId) {
            seededTemplateId.current = activeTemplateId;
            skipNextNavigationAdvance.current = true;
            setFurthestVisitedStep(furthestCompletedStep);
        }
    }, [journeyReady, activeTemplateId, furthestCompletedStep]);

    useEffect(() => {
        if (skipNextNavigationAdvance.current) {
            skipNextNavigationAdvance.current = false;
            return;
        }

        setFurthestVisitedStep((previous) =>
            advanceFurthestVisitedOnNavigation(previous, currentStep, maxReachableStep)
        );
    }, [currentStep, maxReachableStep]);

    const steps = useMemo(
        () =>
            WASTE_RETURN_IMPORT_STEPS.map((id) => ({
                id,
                label: WASTE_RETURN_IMPORT_STEP_LABELS[id],
            })),
        []
    );

    const currentIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(currentStep);
    const furthestIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(furthestVisitedStep);
    const maxReachableIndex = WASTE_RETURN_IMPORT_STEPS.indexOf(maxReachableStep);

    return (
        <Stepper
            steps={steps}
            currentIndex={currentIndex}
            furthestIndex={furthestIndex}
            maxReachableIndex={maxReachableIndex}
            onStepClick={onStepClick}
            ariaLabel="Import progress"
        />
    );
}
